import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { fmtQty, GRADES, QC_RESULT_LABELS, type QcRecord, type QcSummary } from "@/types/inventory";

const tone = (r: string) => (r === "PASS" ? "bg-emerald-500/10 text-emerald-700 border-emerald-300" : r === "SECOND" ? "bg-amber-500/10 text-amber-700 border-amber-300" : "bg-rose-500/10 text-rose-700 border-rose-300");
const dayOffset = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const when = (d: string) => new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function Tile({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tracking-tight mt-1">{value}</p>
      {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}

/** Inspections of rolls, with pass / second / reject counts and the commonest defects */
export function QualityPage() {
  const { error } = useToast();
  const [from, setFrom] = useState(dayOffset(-30));
  const [to, setTo] = useState(dayOffset(0));
  const [result, setResult] = useState("");
  const [search, setSearch] = useUrlSearch();
  const [sum, setSum] = useState<QcSummary | null>(null);

  useEffect(() => {
    api.get(`/api/Qc/Summary?from=${from}&to=${to}`).then((r) => setSum(unwrapObject<QcSummary>(r))).catch(() => setSum(null));
  }, [from, to]);

  const list = usePagedList<QcRecord>({
    deps: [from, to, result, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30", from, to });
      if (result) qs.set("result", result);
      if (search.trim()) qs.set("search", search.trim());
      try {
        return unwrapPage<QcRecord>(await api.get(`/api/Qc/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load inspections");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const t = sum?.totals;
  const pct = (n: number) => (t && t.inspected ? `${Math.round((n / t.inspected) * 100)}%` : "-");

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Quality</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Roll inspections on the four-point system. To inspect a roll, open it in Inventory › Rolls.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" /> to <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" />
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tile label="Inspected" value={t.inspected} hint="Rolls" />
          <Tile label="Passed" value={t.pass} hint={pct(t.pass)} />
          <Tile label="Seconds" value={t.second} hint={pct(t.second)} />
          <Tile label="Rejected" value={t.reject} hint={pct(t.reject)} />
          <Tile label="Avg points" value={t.avg_points ?? "-"} hint="Per 100" />
        </div>
      )}

      {sum && sum.defects.length > 0 && (
        <div className="bg-card border rounded-2xl p-4">
          <h3 className="text-sm font-bold mb-2">Commonest defects</h3>
          <div className="flex flex-wrap gap-2">
            {sum.defects.slice(0, 10).map((d) => (
              <span key={d.defect} className="text-xs rounded-full border px-3 py-1">{d.defect} <b>{d.points}</b> pts · {d.occurrences}×</span>
            ))}
          </div>
        </div>
      )}

      {sum && sum.by_product.length > 0 && (
        <div className="bg-card border rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b"><th className="px-4 py-2">Product</th><th className="px-2 py-2">Inspected</th><th className="px-2 py-2">Pass</th><th className="px-2 py-2">Second</th><th className="px-2 py-2">Reject</th><th className="px-2 py-2">Avg points</th></tr></thead>
            <tbody className="divide-y">{sum.by_product.map((p) => (
              <tr key={p.product_id}><td className="px-4 py-2 font-medium">{p.product_name}</td><td className="px-2 py-2">{p.inspected}</td><td className="px-2 py-2">{p.pass}</td><td className="px-2 py-2">{p.second}</td><td className={`px-2 py-2 ${p.reject ? "text-rose-600 font-semibold" : ""}`}>{p.reject}</td><td className="px-2 py-2">{p.avg_points ?? "-"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by inspection, roll, product or lot…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex gap-1.5">
          {[["", "All"], ["PASS", "Pass"], ["SECOND", "Second"], ["REJECT", "Reject"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setResult(k!)} className={`h-8 px-3 rounded-full text-xs font-semibold border ${result === k ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}>{l}</button>
          ))}
        </div>
      </div>
      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={5} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Inspection</TableHead><TableHead>Roll</TableHead><TableHead>Product · lot</TableHead><TableHead>Defects</TableHead><TableHead className="text-right">Points / 100</TableHead><TableHead>Verdict</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.rows.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="font-mono text-sm font-semibold">{q.inspection_number}<div className="text-[11px] font-sans font-normal text-muted-foreground">{when(q.created_on)}{q.by ? ` · ${q.by}` : ""}</div></TableCell>
                  <TableCell className="text-sm">{q.unit_no}<div className="text-[11px] text-muted-foreground">{fmtQty(q.inspected_qty, q.unit)}</div></TableCell>
                  <TableCell className="text-sm">{q.product_name}<div className="text-[11px] text-muted-foreground">Lot {q.lot_number}</div></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs">{q.defects.length ? q.defects.map((d) => `${d.defect} ${d.count}×${d.points}`).join(", ") : "None found"}{q.notes ? ` · ${q.notes}` : ""}</TableCell>
                  <TableCell className="text-right text-sm font-semibold">{Number(q.points_per_100)}</TableCell>
                  <TableCell><span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${tone(q.result)}`}>{QC_RESULT_LABELS[q.result]}</span>{q.grade_after !== q.grade_before && <div className="text-[11px] text-muted-foreground mt-0.5">{GRADES[q.grade_before]} → {GRADES[q.grade_after]}</div>}</TableCell>
                </TableRow>
              ))}
              {!list.rows.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No inspections in this period</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
      <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
    </div>
  );
}
